import { useMemo, useState } from "react";
import {
  FolderKanban,
  Layers3,
  PencilLine,
  Search,
  ShieldCheck,
  Trash,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import useCategoryRequestStore from "../store/useCategoryRequest";
import ConfirmPopup from "../components/ConfirmPopup";
import Modal from "../components/common/Modal";
import AddNewCategory from "../components/forms/AddNewCategory";
import PaginationComponent from "../components/PaginationComponent";
import {
  useDelCategory,
  useInfiniteGetAllCategory,
} from "../service/categoryService";
import { Button } from "../utils/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../utils/components/ui/dropdown-menu";
import { statusFilterData } from "../utils/constants/constants";
import Loading from "../components/common/LoaderCommon";
import Permission from "../components/auth/Permission";

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-[20px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.07)]">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-[#7B8797]">{title}</p>
          <p className="mt-3 text-[28px] font-semibold tracking-[-0.02em] text-[#1A2B49]">
            {value}
          </p>
          <p className="mt-2 text-sm text-[#8D98A7]">{subtitle}</p>
        </div>
        <div className="rounded-[16px] bg-[#EEF4FF] p-3 text-[#1A5EF9]">
          {icon}
        </div>
      </div>
    </div>
  );
}

function Category() {
  const queryClient = useQueryClient();
  const { setCurrentCategory } = useCategoryRequestStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [status, setStatus] = useState<boolean | null>(null);
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, fetchNextPage, isLoading } = useInfiniteGetAllCategory({
    categoryName: searchKey,
    pageSize: 10,
    pageNo: currentPage,
    status: status,
  });

  const { mutate: deleteCategory } = useDelCategory();

  const categoryData = data?.pages[0]?.categoryResponse || [];
  const totalElements = data?.pages[0]?.totalElements || 0;
  const totalPages = Math.ceil((data?.pages[0]?.totalPages || 0) / 10);

  const mainCategoryCount = useMemo(
    () => categoryData.filter((category) => !category.isSubCategory).length,
    [categoryData],
  );
  const subCategoryCount = useMemo(
    () => categoryData.filter((category) => category.isSubCategory).length,
    [categoryData],
  );
  const activeCount = useMemo(
    () => categoryData.filter((category) => category.status).length,
    [categoryData],
  );

  const handleEdit = (row) => {
    const rowData = row.original;
    setCurrentCategory({
      ...rowData,
      imageUrl: rowData.imageUrl || "active",
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: string | undefined) => {
    deleteCategory(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["useGetAllCategory"] });
        toast.success("Category deleted successfully");
      },
      onError: (error: Error) => {
        toast.error(error.message);
        console.error("Error deleting Category", error);
      },
    });
  };

  const currentStatusLabel =
    status === null
      ? "All Status"
      : statusFilterData.find((option) => option.id === status)?.name ||
        "Status";

  return (
    <div className="min-h-full  py-1">
      <div className="space-y-6">
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h1 className=" text-[34px] font-semibold tracking-[-0.03em] text-[#1A5EF9]">
                Category Management
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-[#7D8998]">
                Organize your catalog with cleaner category structures
              </p>
            </div>
            <Permission roles={["admin"]}>
              <Button
                className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                onClick={() => {
                  setIsModalOpen(true);
                  setCurrentCategory(null);
                }}
              >
                Add New Category
              </Button>
            </Permission>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="relative min-w-[280px] flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
                <input
                  type="text"
                  placeholder="Search a category..."
                  className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  onChange={(e) => {
                    setSearchKey(e.target.value);
                    setCurrentPage(0);
                  }}
                />
              </div>

              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="h-12 min-w-[180px] rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-left text-sm font-medium text-[#5D6E8A] transition hover:border-[#BFD1FF]"
                      type="button"
                    >
                      {currentStatusLabel}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="min-w-[180px]">
                    {statusFilterData.map((option) => (
                      <DropdownMenuItem
                        key={option.id?.toString()}
                        onSelect={() => {
                          setStatus(option.id as boolean);
                          setCurrentPage(0);
                        }}
                      >
                        {option.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {status !== null && (
                  <button
                    className="text-sm font-medium text-[#D14343] transition hover:text-[#AF2B2B]"
                    onClick={() => {
                      setStatus(null);
                      setCurrentPage(0);
                    }}
                    title="Clear status filter"
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-[14px] bg-[#F5F8FD] px-4 py-3 text-sm text-[#7D8998]">
              <span className="font-semibold text-[#253650]">
                {totalElements}
              </span>{" "}
              categories found
            </div>
          </div>
        </div>

        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#21314A]">
                Category Directory
              </h2>
              <p className="mt-1 text-sm text-[#7D8998]">
                Browse, edit, and maintain your main categories and sub
                categories from one view.
              </p>
            </div>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={() => {
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              }}
            />
          </div>

          {isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loading />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-[#F5F8FD] text-left text-sm font-semibold text-[#31425E]">
                    <th className="px-6 py-4">Image</th>
                    <th className="px-6 py-4">Category Name</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Category Type</th>
                    <Permission roles={["admin"]}>
                      <th className="px-6 py-4 text-center">Action</th>
                    </Permission>
                  </tr>
                </thead>
                <tbody>
                  {categoryData.length > 0 ? (
                    categoryData.map((category) => (
                      <tr
                        key={category.id}
                        className="border-t border-[#EEF2FA] text-sm text-[#43536A] transition-colors hover:bg-[#FAFCFF]"
                      >
                        <td className="px-6 py-4">
                          <img
                            src={category.imageUrl || "/images/category.png"}
                            alt=""
                            className="h-10 w-10 rounded-xl object-cover ring-1 ring-[#DFE8F8]"
                          />
                        </td>
                        <td className="px-6 py-4 font-semibold text-[#24344F]">
                          {category.categoryName}
                        </td>
                        <td className="max-w-[360px] px-6 py-4 text-[#6D7C93]">
                          <div className="line-clamp-2">
                            {category.description || "No description added"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              category.status
                                ? "bg-[#EAF2FF] text-[#1A5EF9]"
                                : "bg-[#FDEEEE] text-[#D14343]"
                            }`}
                          >
                            {category.status ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              category.isSubCategory
                                ? "bg-[#E8F7F3] text-[#0C8A61]"
                                : "bg-[#EEF4FF] text-[#275FDA]"
                            }`}
                          >
                            {category.isSubCategory
                              ? "Sub Category"
                              : "Main Category"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <Permission roles={["admin"]}>
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() =>
                                  handleEdit({ original: category })
                                }
                                title="Edit category"
                                aria-label="Edit category"
                                className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
                              >
                                <PencilLine className="size-4" />
                                <span className="sr-only">Edit</span>
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteId(category.id);
                                  setConfirmOpen(true);
                                }}
                                title="Delete category"
                                aria-label="Delete category"
                                className="rounded-[10px] border border-[#F5D4D4] p-2 text-[#B95A5A] transition hover:border-[#EAA7A7] hover:text-[#D14343]"
                              >
                                <Trash className="size-4" />
                                <span className="sr-only">Delete</span>
                              </button>
                            </div>
                          </Permission>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-6 py-16 text-center text-sm text-[#8D98A7]"
                      >
                        No categories match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <ConfirmPopup
            open={confirmOpen}
            setOpen={setConfirmOpen}
            onConfirm={() => handleDelete(deleteId)}
          />
        </div>
      </div>

      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={setIsModalOpen}>
          <AddNewCategory setOpen={setIsModalOpen} />
        </Modal>
      )}
    </div>
  );
}

export default Category;
